import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, CAP_LABELS, fmtErr } from "@/lib/api";
import { wa, WA_MESSAGES } from "@/components/WhatsApp";

const DAY_STYLE = {
  completed: "bg-volt text-black border-volt",
  today: "border-volt text-volt",
  upcoming: "border-zinc-700 text-zinc-300",
  missed: "border-zinc-800 text-zinc-600 line-through",
  rest: "border-zinc-800/60 text-zinc-600",
};

export default function DashboardExtras({ profile, onRefresh }) {
  const [week, setWeek] = useState(null);
  const [logs, setLogs] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ phone: "", age: "", height_cm: "", weight_kg: "", injury: "" });
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    const u = profile.user;
    setForm({
      phone: u.phone || "", age: u.age ?? "", height_cm: u.height_cm ?? "",
      weight_kg: u.weight_kg ?? "", injury: u.injury || "",
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", {
        phone: form.phone.trim(),
        age: form.age === "" ? null : Number(form.age),
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        injury: form.injury.trim(),
      });
      toast.success("Profile updated");
      setEditing(false);
      onRefresh?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    api.get("/me/week").then((r) => setWeek(r.data)).catch(() => {});
    api.get("/me/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/me/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  }, []);

  const payment = profile?.user?.payment;

  return (
    <div className="grid lg:grid-cols-3 gap-4 mt-4" data-testid="dashboard-extras">
      {/* Profile */}
      <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="profile-card">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Profile</p>
        <div className="flex items-center gap-4 mt-4">
          {profile.user.picture ? (
            <img src={profile.user.picture} alt={profile.user.name} className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-volt/15 border border-volt/40 flex items-center justify-center font-extrabold text-volt text-sm">
              {profile.user.name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-bold text-zinc-100">{profile.user.name}</p>
            <p className="text-xs text-zinc-500">{profile.user.email}</p>
          </div>
        </div>
        {editing ? (
          <div className="mt-4 space-y-2.5">
            {[
              ["Phone (Optional)", "phone", "text", "+91 …"],
              ["Age (Optional)", "age", "number", "e.g. 32"],
              ["Height (cm)", "height_cm", "number", "170"],
              ["Weight (kg)", "weight_kg", "number", "70"],
            ].map(([label, key, type, ph]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <label className="text-zinc-500 text-xs uppercase tracking-[0.12em] shrink-0">{label}</label>
                <input type={type} value={form[key]} placeholder={ph} data-testid={`profile-edit-${key}`}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-36 bg-ink border border-zinc-700 px-3 py-2 text-sm text-right focus:border-volt outline-none" />
              </div>
            ))}
            <div>
              <label className="text-zinc-500 text-xs uppercase tracking-[0.12em]">Injury / limitation (Optional)</label>
              <textarea rows={2} value={form.injury} data-testid="profile-edit-injury"
                onChange={(e) => setForm({ ...form, injury: e.target.value })}
                placeholder="Tell us about any injury, pain, movement restriction or physical limitation we should be aware of."
                className="mt-1 w-full bg-ink border border-zinc-700 px-3 py-2 text-sm placeholder-zinc-600 focus:border-volt outline-none" />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={saveProfile} disabled={saving} data-testid="profile-save"
                className="flex-1 bg-volt text-black text-xs uppercase tracking-[0.14em] font-extrabold py-3 hover:bg-white transition-colors disabled:opacity-50">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(false)} data-testid="profile-cancel"
                className="border border-zinc-700 px-4 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <dl className="mt-4 space-y-1.5 text-sm">
              {[
                ["Phone", profile.user.phone],
                ["Age", profile.user.age],
                ["Height", profile.user.height_cm ? `${profile.user.height_cm} cm` : null],
                ["Weight", profile.user.weight_kg ? `${profile.user.weight_kg} kg` : null],
                ["Injury / limitation", profile.user.injury],
                ["Training type", profile.group ? "Group" : "Personalized"],
                ["Assigned coach", profile.group ? "Capable 360 Head Coach" : null],
                ["Current program", profile.group?.name],
                ["Capability score", profile.current ? `${Math.round(Object.values(profile.current).reduce((a, b) => a + b, 0) / 6)} / 100` : null],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-zinc-500 text-xs uppercase tracking-[0.12em]">{label}</dt>
                  <dd className={`text-right ${value ? "text-zinc-200" : "text-zinc-600 italic"}`}>
                    {value ?? "Not provided"}
                  </dd>
                </div>
              ))}
            </dl>
            <button onClick={startEdit} data-testid="profile-edit-open"
              className="mt-4 text-xs uppercase tracking-[0.16em] font-bold text-zinc-400 hover:text-volt transition-colors">
              Edit profile
            </button>
          </>
        )}
      </div>

      {/* Week */}
      <div className="lg:col-span-3 border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="week-strip">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">This week</p>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {week?.days.map((d) => (
            <div key={d.date} data-testid={`week-day-${d.date}`}
              className={`border p-2 sm:p-3 text-center ${DAY_STYLE[d.status]}`}>
              <p className="text-[9px] uppercase tracking-[0.14em] font-bold">{d.weekday}</p>
              <p className="font-display text-xl sm:text-2xl leading-none mt-1">{d.day}</p>
              <p className="text-[8px] uppercase tracking-[0.1em] mt-1 truncate">
                {d.plan ? CAP_LABELS[d.plan.primary_capability] || d.plan.primary_capability : "Rest"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Payments */}
      <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="payments-card">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Payments</p>
        {payment ? (
          <div className="mt-4">
            <p className="font-display uppercase text-2xl leading-none">{payment.plan_name}</p>
            <p className="text-sm text-zinc-400 mt-2">{payment.price} · {payment.billing_cycle}</p>
            <p className="text-xs text-zinc-500 mt-1">Next payment: {payment.next_payment || "—"}</p>
            <span className={`inline-block mt-3 text-[10px] uppercase tracking-[0.16em] font-extrabold border px-2.5 py-1 ${
              payment.status === "paid" ? "text-volt border-volt/50" : payment.status === "overdue" ? "text-red-400 border-red-400/50" : "text-zinc-300 border-zinc-600"}`}
              data-testid="payment-status">
              {payment.status}
            </span>
          </div>
        ) : (
          <p className="text-sm text-zinc-500 mt-4">No plan on file yet — your coach sets this up after your first session.</p>
        )}
      </div>

      {/* Training history */}
      <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="history-card">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Training history</p>
        <p className="font-display text-5xl text-volt mt-3">{logs.length}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">sessions completed</p>
        <div className="mt-4 space-y-2 max-h-44 overflow-y-auto pr-1" style={{ overscrollBehavior: "contain" }}>
          {logs.map((l) => (
            <div key={l.id} className="flex justify-between gap-3 text-sm border-b border-zinc-800/60 pb-2">
              <span className="text-zinc-300">{l.session_name}</span>
              <span className="text-zinc-500 text-xs whitespace-nowrap">{l.date} · {l.completion_pct}%</span>
            </div>
          ))}
          {!logs.length && <p className="text-sm text-zinc-600">Complete today's session to start your log.</p>}
        </div>
      </div>

      {/* Coach responses + contact */}
      <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="coach-contact-card">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Coach responses</p>
        <div className="mt-4 space-y-3 max-h-44 overflow-y-auto pr-1" style={{ overscrollBehavior: "contain" }}>
          {feedback.filter((f) => f.trainer_response).map((f) => (
            <div key={f.id} className="border-l-2 border-volt pl-3">
              <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{f.date}</p>
              <p className="text-sm text-zinc-300 mt-0.5">{f.trainer_response}</p>
            </div>
          ))}
          {!feedback.some((f) => f.trainer_response) && (
            <p className="text-sm text-zinc-600">Replies from your coach appear here.</p>
          )}
        </div>
        <a href={wa(WA_MESSAGES.coach)} target="_blank" rel="noopener noreferrer" data-testid="contact-coach-whatsapp"
          className="mt-5 inline-block border border-[#25D366] text-[#25D366] uppercase tracking-[0.16em] text-xs font-extrabold px-5 py-3 hover:bg-[#25D366] hover:text-black transition-colors">
          Contact coach
        </a>
      </div>
    </div>
  );
}
